class ProductSwiper extends HTMLElement {
    constructor() {
        super();
        this.swipers = {};
        this.observer = null;
        this._videoPlayTimeout = null;
        this._pauseOutOfViewTimeout = null;
        this._onResize = null;
    }

    connectedCallback() {
        this.mount();
        this.observeChanges();
    }

    disconnectedCallback() {
        this._clearVideoTimeout();
        this._clearPauseOutOfViewTimeout();
        if (this._onResize) {
            window.removeEventListener("resize", this._onResize);
            this._onResize = null;
        }
        this.destroySwipers();
        if (this.observer) this.observer.disconnect();
    }

    _clearVideoTimeout() {
        if (this._videoPlayTimeout) {
            clearTimeout(this._videoPlayTimeout);
            this._videoPlayTimeout = null;
        }
    }

    _clearPauseOutOfViewTimeout() {
        if (this._pauseOutOfViewTimeout) {
            clearTimeout(this._pauseOutOfViewTimeout);
            this._pauseOutOfViewTimeout = null;
        }
    }

    _getVideoInSlide(slideEl) {
        return slideEl ? slideEl.querySelector("video") : null;
    }

    _pauseAndResetVideo(slideEl) {
        const video = this._getVideoInSlide(slideEl);
        if (!video) return;
        video.pause();
        video.currentTime = 0;
    }

    _playVideoOnce(slideEl, delayMs = 500) {
        const video = this._getVideoInSlide(slideEl);
        if (!video) return;
        video.loop = false;
        const play = () => {
            video.currentTime = 0;
            video.play().catch(() => { });
        };
        if (delayMs > 0) {
            this._clearVideoTimeout();
            this._videoPlayTimeout = setTimeout(play, delayMs);
        } else {
            play();
        }
    }

    _onSlideChange(swiper) {
        const SLIDE_TRANSITION_MS = 300;
        this._clearVideoTimeout();
        this._clearPauseOutOfViewTimeout();
        const prevSlide = swiper.slides[swiper.previousIndex];
        if (prevSlide) {
            this._pauseOutOfViewTimeout = setTimeout(() => {
                this._pauseAndResetVideo(prevSlide);
                this._pauseOutOfViewTimeout = null;
            }, SLIDE_TRANSITION_MS);
        }
    }

    _onSlideChangeTransitionEnd(swiper) {
        this._clearVideoTimeout();
        const activeSlide = swiper.slides[swiper.activeIndex];
        const video = this._getVideoInSlide(activeSlide);
        if (!video) return;
        this._playVideoOnce(activeSlide, 500);
    }

    _getActiveViewport() {
        return window.matchMedia("(min-width: 1024px)").matches ? "desktop" : "mobile";
    }

    _connectNavigation() {
        const activeViewport = this._getActiveViewport();
        Object.entries(this.swipers).forEach(([viewport, swiper]) => {
            const isActive = viewport === activeViewport;
            swiper.params.navigation.nextEl = isActive ? this.nextButton : null;
            swiper.params.navigation.prevEl = isActive ? this.prevButton : null;
            swiper.navigation.init();
            swiper.navigation.update();
        });
    }

    mount(index = 0) {
        this.container = this.querySelector(".relative.aspect-square");
        if (!this.container || typeof Swiper === "undefined") return;

        this.nextButton = this.container.querySelector('[data-swiper-button="right"]');
        this.prevButton = this.container.querySelector('[data-swiper-button="left"]');

        this.container.querySelectorAll("[data-swiper-product-media]").forEach((swiperElement) => {
            const viewport = swiperElement.dataset.swiperViewport;
            if (!viewport) return;

            const paginationElement = this.container.querySelector(`[data-swiper-pagination="${viewport}"]`);
            const totalSlides = swiperElement.querySelectorAll(".swiper-slide").length;
            if (totalSlides === 0) return;

            this.swipers[viewport] = this.createSwiper(swiperElement, paginationElement, totalSlides, index);
        });

        this._connectNavigation();

        if (!this._onResize) {
            this._onResize = () => this._connectNavigation();
            window.addEventListener("resize", this._onResize);
        }
    }

    createSwiper(swiperElement, paginationElement, totalSlides, index = 0) {
        return new Swiper(swiperElement, {
            initialSlide: index,
            slidesPerView: 'auto',
            spaceBetween: 0,
            speed: 1000,
            centeredSlides: false,
            loop: false,
            keyboard: { enabled: true },
            a11y: { enabled: true },
            observer: true,
            observeParents: true,
            pagination:
                totalSlides > 1 && paginationElement
                    ? {
                        el: paginationElement,
                        clickable: true,
                        bulletClass: "swiper-bullet",
                        bulletActiveClass: "swiper-bullet-active",
                    }
                    : false,

            navigation: {
                nextEl: null,
                prevEl: null,
                disabledClass: "opacity-30 cursor-not-allowed",
            },

            on: {
                init: (swiper) => {
                    swiper.slides.forEach((slide) => this._pauseAndResetVideo(slide));

                    swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.22, 1, 0.36, 1)';

                    const activeSlide = swiper.slides[swiper.activeIndex];
                    const video = this._getVideoInSlide(activeSlide);
                    if (!video) return;
                    this._playVideoOnce(activeSlide, 500);
                },
                setTransition: (swiper) => {
                    swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.22, 1, 0.36, 1)';
                },
                slideChange: (swiper) => {
                    this._onSlideChange(swiper);
                },
                slideChangeTransitionEnd: (swiper) => {
                    this._onSlideChangeTransitionEnd(swiper);
                },
            },
        });
    }

    destroySwipers() {
        this._clearVideoTimeout();
        this._clearPauseOutOfViewTimeout();
        Object.values(this.swipers).forEach((swiper) => {
            swiper.destroy(true, true);
        });
        this.swipers = {};
    }

    observeChanges() {
        this.observer = new MutationObserver(() => {
            if (!Object.keys(this.swipers).length) return;
            this.observer.disconnect();
            this.destroySwipers();
            this.mount();
            this.observer.observe(this, { childList: true, subtree: true });
        });

        this.observer.observe(this, {
            childList: true,
            subtree: true,
        });
    }
}

customElements.define("product-swiper", ProductSwiper);

class ProductSelection extends HTMLElement {
    connectedCallback() {
        this.links = this.querySelectorAll("a[data-fetch-url]");
        this.links.forEach(link => {
            link.addEventListener("click", this.handleClick);
        });
    }



    disconnectedCallback() {
        if (!this.links) return;
        this.links.forEach(link => {
            link.removeEventListener("click", this.handleClick);
        });
    }

    handleClick = async (event) => {
        event.preventDefault();

        const selectedLink = event.currentTarget;
        const url = selectedLink.getAttribute("data-fetch-url");
        const fallbackTitle = selectedLink.getAttribute("data-page-title");
        if (!url) return;

        try {
            const response = await fetch(url, {
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const html = await response.text();

            this.replaceContent(html);
            this.updateUrlParams(url, html, fallbackTitle);


            if (window.matchMedia('(max-width: 768px)').matches) {
                requestAnimationFrame(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                });
            }
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };

    replaceContent(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const section = doc.querySelector("[id^='shopify-section-']") || doc.body;
        const sources = section.querySelectorAll("[data-replace]");

        sources.forEach((source) => {
            const key = source.getAttribute("data-replace");
            if (!key) return;
            const target = document.querySelector(`[data-replace="${key}"]`);
            if (!target) return;
            target.outerHTML = source.outerHTML;
        });
    }

    /**
     * Sync the visible URL with the selected product (e.g. /products/variety-pack → /products/citrus).
     * Strips internal fetch query params like section_id.
     */
    updateUrlParams(fetchUrl, html, fallbackTitle) {
        if (!fetchUrl || typeof window.history.replaceState !== "function") return;
        try {
            const next = new URL(fetchUrl, window.location.origin);
            history.replaceState({}, "", next.pathname);

            let nextTitle = "";
            if (typeof html === "string" && html.trim()) {
                const doc = new DOMParser().parseFromString(html, "text/html");
                nextTitle = doc.querySelector("title")?.textContent?.trim() || "";
            }

            if (!nextTitle && typeof fallbackTitle === "string") {
                nextTitle = fallbackTitle.trim();
            }

            if (nextTitle) {
                document.title = nextTitle;
            }
        } catch {
            /* ignore invalid URL */
        }
    }
}

customElements.define("product-selection", ProductSelection);


class PromoVideo extends HTMLElement {
    constructor() {
        super()
        this.video = this.querySelector('video')
        this.hovering = false
        this.isTouch = null
        this.handleResize = this.handleResize.bind(this)
    }

    connectedCallback() {
        if (!this.video) return

        this.video.muted = true
        this.video.playsInline = true

        this.video.addEventListener('ended', () => {
            if (this.hovering) {
                this.video.currentTime = 0
                this.video.play()
            }
        })

        this.addEventListener('mouseenter', () => {
            if (this.isTouch) return
            this.hovering = true
            this.video.play()
        })

        this.addEventListener('mouseleave', () => {
            if (this.isTouch) return
            this.hovering = false
        })

        window.addEventListener('resize', this.handleResize)

        this.handleResize()
    }

    handleResize() {
        const touch = window.matchMedia('(hover: none)').matches

        if (touch === this.isTouch) return

        this.isTouch = touch

        if (this.isTouch) {
            this.video.loop = true
            this.video.play().catch(() => { })
        } else {
            this.video.loop = false
            this.video.pause()
            this.video.currentTime = 0
        }
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize)
    }
}

customElements.define('promo-video', PromoVideo)